import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useGetCustomerServicesQuery } from "../store/services/flowApi";
import { jwtDecode } from "jwt-decode";

import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  Handle,
  MarkerType,
  Position,
  useNodesState,
  useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";

import {
  useGetFlowByIdQuery,
  useSaveFlowMutation,
  useUpdateFlowMutation,
} from "../store/services/flowApi";

/* ============ CONSTANTS / CONFIG ============ */

const SERVICE_CARD_WIDTH = 220;
const TARGET_WIDTH = 200;

const serviceConfig = {
  sms: {
    label: "Outbound SMS",
    icon: SmsOutlinedIcon,
    bg: "#ffe9f3",
    color: "#ff4fa1",
  },
  rcs: {
    label: "RCS",
    icon: ChatOutlinedIcon,
    bg: "#eef3ff",
    color: "#4a63ff",
  },
  whatsapp: {
    label: "Whatsapp",
    icon: WhatsAppIcon,
    bg: "#e7fbe9",
    color: "#21b35b",
  },
  truecaller: {
    label: "Truecaller",
    icon: PhoneInTalkOutlinedIcon,
    bg: "#e8f1ff",
    color: "#2f8cff",
  },
};

const POST_ACTION_OPTIONS = {
  sms: [
    { key: "delivered", label: "Delivered" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "sent", label: "Sent" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
  whatsapp: [
    { key: "sent", label: "Sent" },
    { key: "delivered", label: "Delivered" },
    { key: "read", label: "Read" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
  truecaller: [
    { key: "sent", label: "Sent" },
    { key: "delivered", label: "Delivered" },
    { key: "read", label: "Read" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
  rcs: [
    { key: "delivered", label: "Delivered" },
    { key: "read", label: "Read" },
    { key: "failed", label: "Failed" },
    { key: "notDeliveredUntil", label: "Awaited" },
    { key: "sent", label: "Sent" },
    { key: "linkClicked", label: "Link Clicked" },
    { key: "linkNotClickedUntil", label: "Link Not Clicked Until" },
  ],
};

const actionPrettyLabels = {
  delivered: "Delivered",
  failed: "Failed",
  read: "Read",
  sent: "Sent",
  notDeliveredUntil: "Awaited",
  linkClicked: "Link Clicked",
  linkNotClickedUntil: "Link Not Clicked Until",
};

/* ============ CUSTOM NODES ============ */

const TargetNode = ({ data }) => {
  return (
    <Box
      sx={{
        width: TARGET_WIDTH,
        bgcolor: "#ffffff",
        borderRadius: 999,
        boxShadow: 2,
        border: "1px solid #dde2ee",
        px: 1.4,
        py: 0.6,
        boxSizing: "border-box",
        fontSize: "0.7rem",
        position: "relative",
        cursor: "default",
      }}
    >
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        style={{
          background: "#4c9fff",
          width: 8,
          height: 8,
          borderRadius: "50%",
        }}
      />

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: "#e9f7ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 0.6,
          }}
        >
          <ChatOutlinedIcon sx={{ fontSize: 11, color: "#27a6ff" }} />
        </Box>

        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
          Target Audience
        </Typography>
      </Box>
    </Box>
  );
};

const ServiceNode = ({ data, id }) => {
  const svcMeta = serviceConfig[data.serviceType] || {};
  const IconComp = svcMeta.icon;
  const selectedActions = data.postActions || [];
  const stepNumber = data.order;
  const isTerminal = data.isTerminal;
  const idLabel =
    data.templateName ||
    data.smsConfig?.templateName ||
    data.whatsappConfig?.templateName ||
    data.rcsConfig?.templateName ||
    data.truecallerConfig?.templateName ||
    data.serviceType?.toUpperCase() ||
    "SVC";

  const getTop = (index, total) => {
    const start = 40;
    const end = 80;
    if (total <= 1) return `${(start + end) / 2}%`;
    const step = (end - start) / (total - 1);
    return `${start + index * step}%`;
  };

  const total = selectedActions.length;

  return (
    <Box
      sx={{
        width: SERVICE_CARD_WIDTH,
        bgcolor: "#ffffff",
        borderRadius: 0.4,
        boxShadow: 2,
        border: "1px solid #dde2ee",
        px: 1,
        py: 0.7,
        boxSizing: "border-box",
        fontSize: "0.7rem",
        position: "relative",
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{
          top: "28%",
          background: "#4c9fff",
          width: 8,
          height: 8,
          borderRadius: "50%",
        }}
      />

      {selectedActions.length > 0 &&
        selectedActions.map((action, index) => {
          const key = action.key || action;
          return (
            <Handle
              key={`R-${key}`}
              type="source"
              position={Position.Right}
              id={`R-${key}`}
              style={{
                top: getTop(index, total),
                background: "#4c9fff",
                width: 8,
                height: 8,
                borderRadius: "50%",
              }}
            />
          );
        })}

      {data.onDeleteNode && (
        <DeleteOutlineIcon
          onClick={(e) => {
            e.stopPropagation();
            data.onDeleteNode();
          }}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            fontSize: 16,
            color: "#999",
            cursor: "pointer",
            "&:hover": { color: "red" },
          }}
        />
      )}

      <Typography
        sx={{ fontSize: "0.55rem", color: "text.secondary", mb: 0.1 }}
      >
        Template Name: {idLabel}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 0.6,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {IconComp && (
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: svcMeta.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 0.6,
              }}
              onClick={(e) => {
                e.stopPropagation();
                data.onOpenConfig?.(id);
              }}
            >
              <IconComp sx={{ fontSize: 11, color: svcMeta.color }} />
            </Box>
          )}
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
            {data.label}
          </Typography>
        </Box>

        {stepNumber && (
          <Box
            sx={{
              minWidth: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: "#e5edff",
              color: "#3554d1",
              fontSize: "0.7rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: 0.5,
            }}
          >
            {stepNumber}
          </Box>
        )}
      </Box>

      {selectedActions.length > 0 && (
        <Box sx={{ mt: 0.4 }}>
          {selectedActions.map((act, i) => {
            const key = act.key || act;
            const minutes = act.minutes;

            return (
              <Box
                key={i}
                sx={{
                  mb: 0.2,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 0.5,
                  border: "1px solid #d4d7e2",
                  bgcolor: "#f7f9ff",
                  px: 0.4,
                  py: 0.18,
                  fontSize: "0.62rem",
                }}
              >
                • {actionPrettyLabels[key] || key}
                {minutes && (
                  <>
                    <AccessTimeIcon sx={{ fontSize: 10, ml: 0.3, mr: 0.2 }} />
                    {minutes}m
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {selectedActions.length === 0 && (
        <Typography
          sx={{
            fontSize: "0.6rem",
            color: "text.disabled",
            mb: 0.3,
          }}
        >
          No post actions added yet
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: "0.6rem",
          color: "#5a76d4",
          mt: 0.2,
          cursor: "pointer",
        }}
        onClick={() => data.onOpenPostActions?.(id)}
      >
        Add Post Actions
      </Typography>
    </Box>
  );
};

const nodeTypes = {
  targetNode: TargetNode,
  serviceNode: ServiceNode,
};

/* ============ POST ACTIONS PANEL ============ */

const PostActionsPanel = ({ node, draft, setDraft, onClose, onSave }) => {
  if (!node) return null;

  const serviceType = node.data.serviceType;
  const serviceLabel = serviceConfig[serviceType]?.label || "Service";

  const handleToggle = (key) => {
    setDraft((prev) =>
      prev.map((item) => {
        if (item.key !== key) return item;

        const nextChecked = !item.checked;

        // When unchecking Awaited, clear its minutes so a stale value
        // doesn't linger and so the mandatory check resets cleanly.
        if (!nextChecked && key === "notDeliveredUntil") {
          return { ...item, checked: false, minutes: "" };
        }

        return { ...item, checked: nextChecked };
      }),
    );
  };

  const handleMinutesChange = (key, value) => {
    setDraft((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, minutes: value } : item,
      ),
    );
  };

  // ── Awaited is the ONLY action whose minutes is mandatory when checked.
  //    Others can be left blank.
  const awaitedItem = draft.find((d) => d.key === "notDeliveredUntil");
  const isAwaitedChecked = awaitedItem?.checked;
  const awaitedMinutes = awaitedItem?.minutes;
  const isAwaitedInvalid =
    isAwaitedChecked &&
    (awaitedMinutes === "" ||
      awaitedMinutes === undefined ||
      awaitedMinutes === null ||
      Number.isNaN(Number(awaitedMinutes)) ||
      Number(awaitedMinutes) < 15);

  const isSaveDisabled = isAwaitedInvalid;

  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 20,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: 300,
          borderRadius: 0.5,
          p: 1.5,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.8,
          }}
        >
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            {serviceLabel} Post Actions
          </Typography>
          <Typography
            sx={{ fontSize: "1rem", cursor: "pointer" }}
            onClick={onClose}
          >
            ×
          </Typography>
        </Box>

        <Typography
          sx={{ fontSize: "0.65rem", color: "text.secondary", mb: 0.8 }}
        >
          ID: {node.id.toUpperCase()}
        </Typography>

        <Stack spacing={0.4} sx={{ mb: 1.2 }}>
          {draft.map((item) => {
            const isAwaited = item.key === "notDeliveredUntil";

            return (
              <Box
                key={item.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  checked={item.checked}
                  onChange={() => handleToggle(item.key)}
                  size="small"
                  sx={{ p: 0.4, mr: 0.5 }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexGrow: 1,
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      borderRadius: 1,
                      border: "1px solid #e1e4ee",
                      bgcolor: "#fafbff",
                      px: 0.6,
                      py: 0.4,
                      fontSize: "0.7rem",
                    }}
                  >
                    {item.label}
                  </Box>

                  {/* Minutes input — shown for any checked action.
                      Mandatory only for Awaited, optional for the rest. */}
                  {item.checked && (
                    <Box
                      sx={{
                        flex: 1,
                        borderRadius: 1,
                        border:
                          isAwaited && isAwaitedInvalid
                            ? "1px solid #e53935"
                            : "1px solid #e1e4ee",
                        bgcolor: "#fff",
                        px: 0.5,
                        py: 0.3,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 14, mr: 0.3 }} />

                      <input
                        type="number"
                        min={isAwaited ? 15 : 0}
                        placeholder={
                          isAwaited ? "Min (required, ≥15)" : "Min (optional)"
                        }
                        value={item.minutes ?? ""}
                        onChange={(e) =>
                          handleMinutesChange(item.key, e.target.value)
                        }
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: "0.7rem",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>

        {isAwaitedInvalid && (
          <Typography
            sx={{ fontSize: "0.65rem", color: "#e53935", mb: 1 }}
          >
            Awaited requires minutes of 15 or more.
          </Typography>
        )}

        <Box sx={{ textAlign: "right" }}>
          <Button
            variant="contained"
            size="small"
            disabled={isSaveDisabled}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
            onClick={onSave}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

/* ============ MAIN EDIT COMPONENT ============ */

const WorkflowEditInner = () => {
  const { cfId } = useParams();
  const navigate = useNavigate();

  const {
    data: flowResp,
    isLoading: isFlowLoading,
    isError,
    refetch: refetchFlow,
  } = useGetFlowByIdQuery(cfId);

  const [saveFlow, { isLoading: isSaving }] = useSaveFlowMutation();
  const [updateFlow, { isLoading: isUpdating }] = useUpdateFlowMutation();

  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [graphLoaded, setGraphLoaded] = useState(false);

  const [postActionNode, setPostActionNode] = useState(null);
  const [postActionDraft, setPostActionDraft] = useState([]);
  const [defaultPostActions, setDefaultPostActions] = useState([]);

  // ── Refresh this page after returning from a channel config screen ──────────
  // onOpenConfig navigates away to /workflow/:cfId/{sms|whatsapp|rcs|truecaller}
  // to edit a node's template/config. That screen's own Save button persists the
  // change on the server and then brings the user back here. Because this page
  // otherwise prefers the localStorage-cached graph (see the effect below), a
  // stale cache would hide whatever was just saved. Setting a flag right before
  // navigating away, and checking it the next time this component mounts, lets
  // us force one full reload to pick up the fresh server data.
  useEffect(() => {
    const flagKey = `workflow-config-return-${cfId}`;
    if (sessionStorage.getItem(flagKey)) {
      sessionStorage.removeItem(flagKey);
      window.location.reload();
    }
  }, [cfId]);

  const terminalNodeIds = useMemo(() => {
    const sourcesWithEdges = new Set(edges.map((e) => e.source));

    return new Set(
      nodes
        .filter((n) => n.type === "serviceNode")
        .filter((n) => !sourcesWithEdges.has(n.id))
        .map((n) => n.id),
    );
  }, [nodes, edges]);

  useEffect(() => {
    if (!graphLoaded) return;

    try {
      const payload = JSON.stringify({ nodes, edges });
      localStorage.setItem(`workflow-edit-graph-${cfId}`, payload);
    } catch (err) {
      console.error("Failed to save edit graph to localStorage", err);
    }
  }, [nodes, edges, cfId, graphLoaded]);

  const cfRow = flowResp?.data;
  const workflowName = cfRow?.cf_name || `Workflow #${cfId}`;
  const workflowCategory = cfRow?.category || "";

  const recomputeServiceOrder = (nodesArr, edgesArr) => {
    const target = nodesArr.find((n) => n.type === "targetNode");
    if (!target) return nodesArr;

    const edgesFrom = {};
    edgesArr.forEach((e) => {
      if (!edgesFrom[e.source]) edgesFrom[e.source] = [];
      edgesFrom[e.source].push(e);
    });

    const visited = new Set();
    const queue = [];

    visited.add(target.id);
    queue.push(target.id);

    let counter = 1;

    const newNodes = nodesArr.map((n) => ({
      ...n,
      data: { ...n.data, order: undefined },
    }));

    const findIndex = (id) => newNodes.findIndex((n) => n.id === id);

    while (queue.length) {
      const currentId = queue.shift();
      const outEdges = edgesFrom[currentId] || [];

      outEdges.forEach((edge) => {
        if (visited.has(edge.target)) return;

        visited.add(edge.target);
        queue.push(edge.target);

        const idx = findIndex(edge.target);
        if (idx !== -1 && newNodes[idx].type === "serviceNode") {
          newNodes[idx] = {
            ...newNodes[idx],
            data: {
              ...newNodes[idx].data,
              order: counter,
            },
          };
          counter += 1;
        }
      });
    }

    return newNodes;
  };

  const onEdgeDoubleClick = useCallback(
    (event, edge) => {
      event.stopPropagation();

      setEdges((eds) => {
        const nextEdges = eds.filter((e) => e.id !== edge.id);

        setNodes((nds) => recomputeServiceOrder(nds, nextEdges));

        return nextEdges;
      });
    },
    [setEdges, setNodes],
  );

  useEffect(() => {
    if (!cfRow) return;

    const key = `workflow-edit-graph-${cfId}`;

    try {
      const saved = localStorage.getItem(key);

      if (saved) {
        const parsed = JSON.parse(saved);
        const savedNodes = parsed.nodes || [];
        const savedEdges = parsed.edges || [];

        if (savedNodes.length || savedEdges.length) {
          setNodes(savedNodes);
          setEdges(savedEdges);
          setGraphLoaded(true);
          return;
        }
      }
    } catch (err) {
      console.error("[WF EDIT] Error reading localStorage graph", err);
    }

    if (!Array.isArray(cfRow.flow_data) || cfRow.flow_data.length === 0) {
      setNodes([]);
      setEdges([]);
      setGraphLoaded(true);
      return;
    }

    const flowData = cfRow.flow_data;
    const tempNodes = [];
    const tempEdges = [];
    const targetId = "target-1";
    tempNodes.push({
      id: targetId,
      type: "targetNode",
      position: { x: 50, y: 200 },
      data: {
        nodeId: targetId,
        label: "Target Audience",
        stats: { sent: 0, delivered: 0, failed: 0 },
        targetFile: null,
      },
    });

    const serviceIdToNodeId = {};

    flowData.forEach((svc, index) => {
      const nodeId = `svc-${svc.orderid}`;
      serviceIdToNodeId[svc.orderid] = nodeId;

      const serviceType = svc.serviceType;
      const cfg = svc.data || {};

      const configMeta = serviceConfig[serviceType] || {};
      const defaultLabel =
        configMeta.label || serviceType?.toUpperCase() || "Service";

      const templateName = cfg.templateName || "";

      const nodeData = {
        nodeId,
        serviceType,
        label: templateName || defaultLabel,
        postActions: svc.postActions || [],
        stats: { sent: 0, delivered: 0, failed: 0 },
        templateName,

        smsConfig: serviceType === "sms" ? cfg : undefined,
        whatsappConfig: serviceType === "whatsapp" ? cfg : undefined,
        rcsConfig: serviceType === "rcs" ? cfg : undefined,
        truecallerConfig: serviceType === "truecaller" ? cfg : undefined,
      };

      const position = {
        x: 250 + index * 260,
        y: 200,
      };

      tempNodes.push({
        id: nodeId,
        type: "serviceNode",
        position,
        data: nodeData,
      });
    });

    const hasIncoming = new Set();

    flowData.forEach((svc) => {
      (svc.connections || []).forEach((conn) => {
        hasIncoming.add(conn.to);
      });
    });

    flowData.forEach((svc) => {
      if (!hasIncoming.has(svc.orderid)) {
        const targetEdge = {
          id: `e-${targetId}-${serviceIdToNodeId[svc.orderid]}-sent`,
          source: targetId,
          target: serviceIdToNodeId[svc.orderid],
          sourceHandle: "R-sent",
          targetHandle: "in",
          animated: false,
          type: "smoothstep",
          style: {
            stroke: "#4c9fff",
            strokeWidth: 1.5,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 18,
            height: 18,
          },
        };
        tempEdges.push(targetEdge);
      }
    });

    flowData.forEach((svc) => {
      const fromNodeId = serviceIdToNodeId[svc.orderid];
      if (!fromNodeId) return;

      (svc.connections || []).forEach((conn) => {
        const toNodeId = serviceIdToNodeId[conn.to];
        if (!toNodeId) return;

        const via = conn.via || "sent";

        tempEdges.push({
          id: `e-${fromNodeId}-${toNodeId}-${via}`,
          source: fromNodeId,
          target: toNodeId,
          sourceHandle: `R-${via}`,
          targetHandle: "in",
          animated: false,
          type: "smoothstep",
          style: {
            stroke: "#4c9fff",
            strokeWidth: 1.5,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 18,
            height: 18,
          },
        });
      });
    });

    const orderedNodes = recomputeServiceOrder(tempNodes, tempEdges);

    setNodes(orderedNodes);
    setEdges(tempEdges);
    setGraphLoaded(true);
  }, [cfId, cfRow, setNodes, setEdges]);

  const [customerId, setCustomerId] = useState("");

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");

      if (!token) {
        console.error("Access token not found");
        return;
      }

      const decoded = jwtDecode(token);

      const id =
        decoded?.customer_id ||
        decoded?.id ||
        decoded?.userid ||
        "";

      if (!id) {
        console.error("Customer ID not found in token", decoded);
        return;
      }

      setCustomerId(String(id));
    } catch (err) {
      console.error("Invalid access token format", err);
    }
  }, []);

  const { data: serviceResp, isLoading: isServiceLoading } =
    useGetCustomerServicesQuery(customerId, {
      skip: !customerId,
    });

  const closePostActions = () => {
    setPostActionNode(null);
    setPostActionDraft([]);
  };

  const openPostActions = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node || node.type !== "serviceNode") return;

      const serviceType = node.data.serviceType;
      const options = POST_ACTION_OPTIONS[serviceType] || [];
      const currentSelected = node.data.postActions || [];

      const draft = options.map((opt) => {
        const found = currentSelected.find(
          (a) => (a.key || a) === opt.key,
        );

        return {
          ...opt,
          checked: !!found,
          // Preserve any previously-saved minutes value (string or empty)
          minutes:
            found && found.minutes !== undefined && found.minutes !== null
              ? String(found.minutes)
              : "",
        };
      });

      setPostActionNode(node);
      setPostActionDraft(draft);
    },
    [nodes],
  );

  const savePostActions = () => {
    if (!postActionNode) return;

    const selectedKeys = postActionDraft
      .filter((d) => d.checked)
      .map((d) => {
        const entry = { key: d.key };

        // Attach minutes only when the user actually entered a positive number.
        // For Awaited, the panel already guarantees a valid ≥15 value before
        // Save is enabled; for others it's entirely optional.
        const rawMinutes = d.minutes;
        if (
          rawMinutes !== "" &&
          rawMinutes !== undefined &&
          rawMinutes !== null
        ) {
          const num = Number(rawMinutes);
          if (!Number.isNaN(num) && num > 0) {
            entry.minutes = num;
          }
        }

        return entry;
      });

    setNodes((nds) =>
      nds.map((n) =>
        n.id === postActionNode.id
          ? {
            ...n,
            data: {
              ...n.data,
              postActions: selectedKeys,
            },
          }
          : n,
      ),
    );

    if (defaultPostActions.length === 0 && selectedKeys.length > 0) {
      setDefaultPostActions(selectedKeys);
    }

    closePostActions();
  };

  const availableServices = useMemo(() => {
    if (!serviceResp?.data) return [];

    const map = new Map();

    serviceResp.data.forEach((svc) => {
      if (Number(svc.show_service) !== 1) return;

      const key = svc.service_name.toLowerCase();

      if (!map.has(key) && serviceConfig[key]) {
        map.set(key, {
          serviceType: key,
          label: serviceConfig[key].label,
          icon: serviceConfig[key].icon,
          bg: serviceConfig[key].bg,
          color: serviceConfig[key].color,
        });
      }
    });

    return Array.from(map.values());
  }, [serviceResp]);

  const nodesWithCallbacks = useMemo(
    () =>
      nodes.map((n) =>
        n.type === "serviceNode"
          ? {
            ...n,
            data: {
              ...n.data,
              isTerminal: terminalNodeIds.has(n.id),
              onOpenPostActions: openPostActions,

              onDeleteNode: () => {
                setNodes((nds) => nds.filter((node) => node.id !== n.id));
                setEdges((eds) =>
                  eds.filter(
                    (edge) =>
                      edge.source !== n.id && edge.target !== n.id,
                  ),
                );
              },

              onOpenConfig: () => {
                const nodeId = n.id;
                const type = n.data.serviceType;

                // Flag this workflow so that when the user comes back from the
                // channel config screen (after hitting its Save button), the
                // effect above forces one reload and picks up the freshly
                // saved template/config instead of the stale cached graph.
                sessionStorage.setItem(
                  `workflow-config-return-${cfId}`,
                  "1",
                );

                // Use a FULL browser navigation (not react-router's navigate)
                // to open the channel config screen. Client-side navigation
                // keeps the same Redux/RTK-Query store alive, so that screen
                // can show whatever it had cached from the last visit instead
                // of the template/config that was just saved. A full page
                // load clears the store, forcing it to fetch fresh data every
                // time — matching what happens on a manual refresh.
                if (type === "sms") {
                  window.location.href = `/workflow/${cfId}/sms?edit=true&nodeId=${nodeId}`;
                } else if (type === "whatsapp") {
                  window.location.href = `/workflow/${cfId}/whatsapp?edit=true&nodeId=${nodeId}`;
                } else if (type === "rcs") {
                  window.location.href = `/workflow/${cfId}/rcs?edit=true&nodeId=${nodeId}`;
                } else if (type === "truecaller") {
                  window.location.href = `/workflow/${cfId}/truecaller?edit=true&nodeId=${nodeId}`;
                }
              },
            },
          }
          : n,
      ),
    [nodes, openPostActions, navigate, cfId, terminalNodeIds],
  );

  const handlePaletteDragStart = (e, payload) => {
    e.dataTransfer.setData("application/block-type", payload.type);
    if (payload.serviceType) {
      e.dataTransfer.setData("application/service-type", payload.serviceType);
    }
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowWrapper.current || !reactFlowInstance) return;

      const type = event.dataTransfer.getData("application/block-type");
      const serviceType = event.dataTransfer.getData(
        "application/service-type",
      );

      if (!type) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      if (type === "target") {
        const alreadyTarget = nodes.some((n) => n.type === "targetNode");
        if (alreadyTarget) return;

        const id = `target-${Date.now()}`;
        const newNode = {
          id,
          type: "targetNode",
          position,
          data: {
            nodeId: id,
            label: "Target Audience",
            stats: { sent: 0, delivered: 0, failed: 0 },
            targetFile: null,
          },
        };
        setNodes((nds) => nds.concat(newNode));
        return;
      }

      const config = serviceConfig[serviceType] || {};
      const label = config.label || "Service";

      const id = `svc-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;

      const initialPostActions =
        defaultPostActions && defaultPostActions.length > 0
          ? defaultPostActions
          : [];

      const newNode = {
        id,
        type: "serviceNode",
        position,
        data: {
          nodeId: id,
          serviceType,
          label,
          stats: { sent: 0, delivered: 0, failed: 0 },
          postActions: initialPostActions,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, nodes, setNodes, defaultPostActions],
  );

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const newEdges = addEdge(
          {
            ...params,
            animated: false,
            type: "smoothstep",
            style: {
              stroke: "#4c9fff",
              strokeWidth: 1.5,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 18,
              height: 18,
            },
          },
          eds,
        );

        setNodes((nds) => recomputeServiceOrder(nds, newEdges));
        return newEdges;
      });
    },
    [setEdges, setNodes],
  );

  const executeWorkflow = async () => {
    if (!workflowName?.trim()) {
      alert("Workflow name missing hai (cf_name).");
      return;
    }

    const currentNodes = nodes;
    const currentEdges = edges;

    const target = currentNodes.find((n) => n.type === "targetNode");
    if (!target) {
      alert("Please drag Target Audience first.");
      return;
    }
    if (currentNodes.length === 1) {
      alert("Add at least one channel block.");
      return;
    }

    const BASE_NUMBERS = 1000;
    const incoming = {};
    currentNodes.forEach((n) => {
      incoming[n.id] = 0;
    });
    incoming[target.id] = BASE_NUMBERS;

    const edgesFrom = {};
    currentEdges.forEach((e) => {
      if (!edgesFrom[e.source]) edgesFrom[e.source] = [];
      edgesFrom[e.source].push(e);
    });

    const updatedNodes = currentNodes.map((n) => ({
      ...n,
      data: { ...n.data, stats: { sent: 0, delivered: 0, failed: 0 } },
    }));

    const findIndex = (id) => updatedNodes.findIndex((n) => n.id === id);

    const queue = [target.id];

    while (queue.length) {
      const currentId = queue.shift();
      const idx = findIndex(currentId);
      if (idx === -1) continue;

      const node = updatedNodes[idx];
      const inCount = incoming[currentId] || 0;
      if (inCount === 0) continue;

      let sent = inCount;
      let delivered = 0;
      let failed = 0;

      if (node.type === "targetNode") {
        delivered = 0;
        failed = 0;
      } else {
        delivered = Math.round(sent * 0.8);
        failed = sent - delivered;
      }

      node.data = { ...node.data, stats: { sent, delivered, failed } };

      const outEdges = edgesFrom[currentId] || [];
      outEdges.forEach((edge) => {
        const rawHandle = edge.sourceHandle || "sent";
        const key = rawHandle.replace(/^R-/, "").replace(/^L-/, "");

        let metricKey = "sent";
        if (key === "delivered") metricKey = "delivered";
        else if (key === "failed") metricKey = "failed";

        const nodeStats = node.data.stats || {};
        const amount = nodeStats[metricKey] ?? nodeStats.sent ?? 0;

        if (!incoming[edge.target]) incoming[edge.target] = 0;
        incoming[edge.target] += amount;

        if (!queue.includes(edge.target)) queue.push(edge.target);
      });
    }

    setNodes(updatedNodes);

    const serviceNodes = updatedNodes.filter((n) => n.type === "serviceNode");

    const serviceNodesSorted = [...serviceNodes].sort((a, b) => {
      const oa = a.data.order ?? 99999;
      const ob = b.data.order ?? 99999;
      if (oa === ob) return 0;
      return oa - ob;
    });

    const stepMap = {};
    serviceNodesSorted.forEach((node, idx) => {
      const stepNumber = idx + 1;
      stepMap[node.id] = stepNumber;
    });

    const servicesPayload = serviceNodesSorted.map((n) => {
      let configData = {};

      if (n.data.serviceType === "sms") {
        const cfg = n.data.smsConfig || {};

        configData = {
          principalentityId:
            cfg.principalentityId ?? cfg.entityId ?? cfg.peId ?? "",
          entityId: cfg.entityId ?? "",
          peId: cfg.peId ?? "",
          senderId: cfg.senderId ?? "",
          templateId: cfg.templateId ?? "",
          templateName: cfg.templateName ?? "",
          message: cfg.message ?? "",
          filterDatatill_days: cfg.filterDatatill_days ?? 0,
        };
      } else if (n.data.serviceType === "whatsapp") {
        configData = n.data.whatsappConfig || {};
      } else if (n.data.serviceType === "rcs") {
        configData = n.data.rcsConfig || {};
      } else if (n.data.serviceType === "truecaller") {
        configData = n.data.truecallerConfig || {};
      }

      const outEdges = edgesFrom[n.id] || [];

      const connections = outEdges
        .filter((e) => stepMap[e.target] != null)
        .map((e) => {
          const rawHandle = e.sourceHandle || "sent";
          const key = rawHandle.replace(/^R-/, "").replace(/^L-/, "");
          return {
            to: stepMap[e.target],
            via: key,
          };
        });

      return {
        orderid: stepMap[n.id],
        serviceType: n.data.serviceType,
        postActions: n.data.postActions || [],
        connections,
        data: configData,
      };
    });

    try {
      const resp = await updateFlow({
        cf_id: Number(cfId),
        cf_name: workflowName,
        category: workflowCategory,
        flows: servicesPayload,
      }).unwrap();
      await refetchFlow();

      alert("Workflow updated & saved to server successfully!");
      navigate("/workflow");
    } catch (err) {
      console.error("Failed to update flow on server:", err);
      alert(
        "Failed to update workflow on server. Please try again. Error: " +
        (err?.data?.message || err.message),
      );
    }
  };

  const isEmpty = nodes.length === 0;

  if (isFlowLoading) {
    return (
      <Box p={3}>
        <Typography sx={{ fontSize: "0.85rem" }}>Loading workflow...</Typography>
      </Box>
    );
  }

  if (isError || !cfRow) {
    return (
      <Box p={3}>
        <Typography color="error" sx={{ fontSize: "0.85rem" }}>
          Failed to load workflow detail. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 64px)",
        bgcolor: "#eef1f7",
      }}
    >
      {/* MAIN AREA */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* TOP HEADER */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.25,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: 1.75,
              py: 0.5,
              borderRadius: 999,
              bgcolor: "#ffffff",
              minWidth: 180,
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
              {workflowName}
            </Typography>
          </Paper>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: "none", fontSize: "0.78rem" }}
              onClick={() => navigate("/workflow")}
            >
              Back
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ textTransform: "none", fontSize: "0.78rem" }}
              onClick={executeWorkflow}
              disabled={isUpdating}
            >
              {isUpdating ? "Updating..." : "Update & Execute"}
            </Button>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, display: "flex", gap: 1.5, px: 1.5, pb: 1.5 }}>
          <Paper
            elevation={0}
            sx={{
              flexGrow: 1,
              position: "relative",
              bgcolor: "#f5f7fb",
              borderRadius: 2,
              border: "1px solid #dde2ee",
              overflow: "hidden",
            }}
          >
            <Box
              ref={reactFlowWrapper}
              sx={{ width: "100%", height: "100%" }}
              onDrop={onDrop}
              onDragOver={onDragOver}
            >
              <ReactFlow
                nodes={nodesWithCallbacks}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                nodeTypes={nodeTypes}
                fitView
                proOptions={{ hideAttribution: true }}
                onEdgeDoubleClick={onEdgeDoubleClick}
              >
                <Background />
                <MiniMap />
                <Controls />
              </ReactFlow>

              {isEmpty && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "text.secondary",
                    pointerEvents: "none",
                  }}
                >
                  <Typography sx={{ fontSize: "0.8rem", mb: 0.6 }}>
                    Drag <strong>Target Audience</strong> from right panel.
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem" }}>
                    Then drag Outbound SMS / WhatsApp / RCS / Truecaller cards
                    to modify the journey.
                  </Typography>
                </Box>
              )}

              <PostActionsPanel
                node={postActionNode}
                draft={postActionDraft}
                setDraft={setPostActionDraft}
                onClose={closePostActions}
                onSave={savePostActions}
              />
            </Box>
          </Paper>

          <Paper
            elevation={1}
            sx={{
              width: 230,
              borderRadius: 2,
              bgcolor: "#ffffff",
              p: 1.25,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.6 }}>
              Journey Builder (Edit)
            </Typography>

            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                mb: 0.3,
                color: "text.secondary",
              }}
            >
              Starting Points
            </Typography>

            <Box
              draggable
              onDragStart={(e) => handlePaletteDragStart(e, { type: "target" })}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 1.2,
                cursor: "grab",
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: "#e9f7ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1,
                }}
              >
                <ChatOutlinedIcon sx={{ fontSize: 14, color: "#27a6ff" }} />
              </Box>
              <Typography sx={{ fontSize: "0.75rem", color: "#8f9bb3" }}>
                Target Audience
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                mb: 0.3,
                color: "text.secondary",
              }}
            >
              Outbound Channels
            </Typography>

            {isServiceLoading && (
              <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                Loading services...
              </Typography>
            )}

            {!isServiceLoading && availableServices.length === 0 && (
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                You have not been assigned any service. Please contact your
                administrator.
              </Typography>
            )}

            <Stack spacing={1.2} sx={{ mb: 1.2 }}>
              {availableServices.map((svc) => {
                const Icon = svc.icon;

                return (
                  <Box
                    key={svc.serviceType}
                    draggable
                    onDragStart={(e) =>
                      handlePaletteDragStart(e, {
                        type: "service",
                        serviceType: svc.serviceType,
                      })
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "grab",
                    }}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        bgcolor: svc.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 0.75,
                      }}
                    >
                      <Icon sx={{ fontSize: 14, color: svc.color }} />
                    </Box>

                    <Typography sx={{ fontSize: "0.72rem" }}>
                      {svc.label}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>

            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                mb: 0.3,
                color: "text.secondary",
              }}
            >
              Flow Controls
            </Typography>

            <Stack direction="row" spacing={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "default",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "#fff4e3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 0.75,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 14, color: "#ff9800" }} />
                </Box>
                <Typography sx={{ fontSize: "0.72rem" }}>Wait</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "default",
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "#ffe9f4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 0.75,
                  }}
                >
                  <CompareArrowsIcon sx={{ fontSize: 14, color: "#e0419c" }} />
                </Box>
                <Typography sx={{ fontSize: "0.72rem" }}>
                  Switch Identifier
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const WorkflowEdit = () => (
  <ReactFlowProvider>
    <WorkflowEditInner />
  </ReactFlowProvider>
);

export default WorkflowEdit;